/**
 * @tanstack/react-query hooks for the Timesheet module (G3) project +
 * assignment endpoints. Thin wrappers over lib/api/timesheet.ts so the
 * pages stay declarative and caching/invalidation lives in one place.
 *
 * Query-key convention mirrors use-hr.ts / use-holidays.ts:
 *   ["timesheet", "projects", "list", includeInactive]
 *   ["timesheet", "projects", "detail", id]
 *   ["timesheet", "projects", id, "assignments"]
 *
 * The shared QueryClient (components/providers.tsx) sets retry: 1,
 * refetchOnWindowFocus: false, staleTime: 30s. Projects change rarely
 * (an HR admin sets up the catalogue once, then occasional edits), so
 * we lean on a longer CATALOGUE_STALE for the list + detail.
 *
 * The list endpoint is reachable by any authed tenant user (server-
 * scoped); the page itself is HR-admin only — so this hook also serves
 * the (future) employee weekly-grid project picker. Mutations 403 a
 * non-admin server-side; the page is the UX gate.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { timesheetApi } from "@/lib/api";
import { invalidateApprovalsSummary } from "@/lib/approvals-invalidate";
import type {
  CreateProjectRequest,
  GetWeekQuery,
  ListProjectsQuery,
  TimeEntryInput,
  TimesheetWeekStatus,
  UpdateProjectRequest,
  UpdateTimesheetSettingsRequest,
  WeekResponse,
} from "@/types/timesheet";

// 5 minutes — projects are a slow-moving catalogue (occasional edits).
const PROJECTS_STALE = 5 * 60 * 1000;
// Weeks are the editable surface — short stale so a fresh navigation
// reflects e.g. an admin's regularisation unlock. The PUT/POST mutations
// invalidate explicitly; this is just the background freshness floor.
const WEEKS_STALE = 30 * 1000;

export const timesheetKeys = {
  all: ["timesheet"] as const,
  projects: () => [...timesheetKeys.all, "projects"] as const,
  projectsList: (includeInactive?: boolean) =>
    [
      ...timesheetKeys.projects(),
      "list",
      includeInactive ? "with-inactive" : "active-only",
    ] as const,
  project: (id: number) =>
    [...timesheetKeys.projects(), "detail", id] as const,
  assignments: (id: number) =>
    [...timesheetKeys.projects(), id, "assignments"] as const,
  // Week queries — cached per (weekStart, employee_id). employee_id is
  // explicitly normalised to a string "own" for the caller's own week so
  // the cache key is stable across renders (vs an undefined/0 mix).
  weeks: () => [...timesheetKeys.all, "weeks"] as const,
  week: (weekStart: string, employeeId?: number) =>
    [
      ...timesheetKeys.weeks(),
      weekStart,
      employeeId ?? "own",
    ] as const,
  // Manager approval inbox (G3 T.3b). Cached per status tab; the bucket
  // is invalidated en-bloc after any approve/reject/unlock so the moving
  // row lands in its new tab on the next render.
  inbox: () => [...timesheetKeys.all, "inbox"] as const,
  inboxList: (status?: TimesheetWeekStatus, excludeSelf?: boolean) =>
    [
      ...timesheetKeys.inbox(),
      status ?? "submitted",
      excludeSelf ? "excl-self" : "role",
    ] as const,
  // Tenant timesheet settings (085 / docs/47) — loose/strict + standard
  // work minutes. A slow-moving tenant-level config (HR-admin sets it
  // once); the PATCH mutation invalidates explicitly.
  settings: () => [...timesheetKeys.all, "settings"] as const,
};

// ---------------------------------------------------------------------------
// Projects — list / detail (any authed; server-scoped)
// ---------------------------------------------------------------------------

export function useProjects(query?: ListProjectsQuery) {
  return useQuery({
    queryKey: timesheetKeys.projectsList(query?.include_inactive),
    queryFn: ({ signal }) =>
      timesheetApi.listProjects(query, signal),
    staleTime: PROJECTS_STALE,
  });
}

export function useProject(id: number, enabled = true) {
  return useQuery({
    queryKey: timesheetKeys.project(id),
    queryFn: ({ signal }) => timesheetApi.getProject(id, signal),
    enabled: enabled && Number.isFinite(id) && id > 0,
    staleTime: PROJECTS_STALE,
  });
}

// ---------------------------------------------------------------------------
// Projects — mutations (HR admin server-side; UX gate in-page)
// ---------------------------------------------------------------------------

/**
 * Flush every projects bucket after a mutation. A new / edited / soft-
 * deleted project changes whichever (include_inactive) list bucket we
 * happen to have cached + a specific detail; we flush the whole
 * projects root rather than guess which bucket moved.
 */
function invalidateProjects(
  qc: ReturnType<typeof useQueryClient>,
) {
  void qc.invalidateQueries({ queryKey: timesheetKeys.projects() });
}

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateProjectRequest) =>
      timesheetApi.createProject(body),
    onSuccess: () => invalidateProjects(qc),
  });
}

export function useUpdateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; body: UpdateProjectRequest }) =>
      timesheetApi.updateProject(vars.id, vars.body),
    onSuccess: () => invalidateProjects(qc),
  });
}

export function useDeleteProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => timesheetApi.deleteProject(id),
    onSuccess: () => invalidateProjects(qc),
  });
}

// ---------------------------------------------------------------------------
// Assignments — list / replace (HR admin server-side)
// ---------------------------------------------------------------------------

export function useProjectAssignments(projectId: number, enabled = true) {
  return useQuery({
    queryKey: timesheetKeys.assignments(projectId),
    queryFn: ({ signal }) =>
      timesheetApi.listAssignments(projectId, signal),
    enabled:
      enabled && Number.isFinite(projectId) && projectId > 0,
    // Retry: false — a non-admin caller would 403 here; don't spin.
    retry: false,
  });
}

/**
 * Replace the project's assignments. On success, seed the cache with the
 * server-returned list so the page reflects the new set without an
 * extra round-trip, then flush both the assignments query and the
 * project list (assigned-count on the list row moves too).
 */
export function useReplaceProjectAssignments() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { projectId: number; employeeIds: number[] }) =>
      timesheetApi.replaceAssignments(
        vars.projectId,
        vars.employeeIds,
      ),
    onSuccess: (data, vars) => {
      qc.setQueryData(
        timesheetKeys.assignments(vars.projectId),
        data,
      );
      // List rows show assigned-count derived from this query → flush.
      void qc.invalidateQueries({
        queryKey: timesheetKeys.projects(),
      });
    },
  });
}

// ---------------------------------------------------------------------------
// Weekly grid — get / upsert entries / submit (G3 T.3a employee surface)
// ---------------------------------------------------------------------------

/**
 * The bucket for a given (weekStart, employee_id?). Returns the
 * timesheet_weeks row + entries + leave/holiday overlays + the project
 * catalogue the caller can log against. Auto-creates a draft shell row
 * server-side; valid weeks never 404.
 */
export function useWeek(weekStart: string, opts?: GetWeekQuery) {
  const employeeId = opts?.employee_id;
  return useQuery<WeekResponse>({
    queryKey: timesheetKeys.week(weekStart, employeeId),
    queryFn: ({ signal }) =>
      timesheetApi.getWeek(weekStart, opts, signal),
    enabled: !!weekStart,
    staleTime: WEEKS_STALE,
    // The week endpoint can 404 transiently while the backend T.2b is
    // being deployed in parallel — don't loop the retry; render the
    // graceful empty state once and let the user retry by navigating.
    retry: 1,
  });
}

/**
 * PUT /api/v1/timesheet/entries — replace-on-week. Seeds the cache with
 * the server response (which has recomputed totals + the rejected→draft
 * auto-unlock) so the grid doesn't flash a stale local state between
 * mutate and refetch.
 */
export function useUpsertEntries(weekStart: string, employeeId?: number) {
  const qc = useQueryClient();
  return useMutation<
    WeekResponse,
    unknown,
    TimeEntryInput[]
  >({
    mutationFn: (entries: TimeEntryInput[]) =>
      timesheetApi.upsertEntries(weekStart, entries),
    onSuccess: (data) => {
      qc.setQueryData(
        timesheetKeys.week(weekStart, employeeId),
        data,
      );
      // A neighbouring-week cache (e.g. last-week's project list for the
      // copy-from-last-week affordance) is untouched — these are
      // per-week buckets and only the active week changed.
    },
  });
}

/**
 * POST /api/v1/timesheet/weeks/{weekStart}/submit — transitions
 * draft→submitted. Same cache-seed pattern as upsert so the grid flips
 * to read-only without an extra round-trip.
 */
export function useSubmitWeek(weekStart: string, employeeId?: number) {
  const qc = useQueryClient();
  return useMutation<WeekResponse, unknown, void>({
    mutationFn: () => timesheetApi.submitWeek(weekStart),
    onSuccess: (data) => {
      qc.setQueryData(
        timesheetKeys.week(weekStart, employeeId),
        data,
      );
      // An HR admin's own submitted week is in their timesheet badge.
      invalidateApprovalsSummary(qc);
    },
  });
}

// ---------------------------------------------------------------------------
// Manager approval inbox + state-machine actions (G3 T.3b — built against
// the fixed T.2c contract).
//
// Cache invalidation contract:
//  - On approve / reject / unlock we ALWAYS invalidate the entire inbox
//    root (all three tabs) — the affected row literally moves between
//    tabs (submitted → approved/rejected, approved → draft).
//  - We ALSO seed the specific (weekStart, employee_id) week cache with
//    the server-returned WeekResponse so the manager detail page re-
//    renders immediately with the new status (no detour through a
//    separate refetch).
// ---------------------------------------------------------------------------

/**
 * The submitted/approved/rejected inbox bucket for the requester. The
 * SQL is server-scoped (manager-of OR hr_admin); plain employees with
 * no reports get an empty list — the page renders the empty-state
 * panel rather than gating the route. retry:1 mirrors the leave-inbox
 * convention: a transient 4xx degrades to the empty state once rather
 * than hammering.
 */
export function useInbox(
  status?: TimesheetWeekStatus,
  opts?: { excludeSelf?: boolean },
) {
  return useQuery({
    queryKey: timesheetKeys.inboxList(status, opts?.excludeSelf),
    queryFn: ({ signal }) =>
      timesheetApi.getInbox(
        {
          ...(status ? { status } : {}),
          ...(opts?.excludeSelf ? { exclude_self: true } : {}),
        },
        signal,
      ),
    // 60s — the inbox is a moving target (submissions land continuously)
    // but the cost of a stale "Submitted" tab for a minute is small
    // compared to refetching on every tab switch. The mutations
    // explicitly invalidate, so the freshness contract holds.
    staleTime: 60 * 1000,
    retry: 1,
  });
}

/**
 * Approve a submitted week. The server returns the full WeekResponse
 * (now `approved` + approver fields populated); we seed the week-detail
 * cache so the manager detail page reflects the new state without an
 * extra round-trip, then invalidate the inbox so the row jumps to the
 * Approved tab.
 */
export function useApproveWeek(weekStart: string, employeeId: number) {
  const qc = useQueryClient();
  return useMutation<WeekResponse, unknown, void>({
    mutationFn: () => timesheetApi.approveWeek(weekStart, employeeId),
    onSuccess: (data) => {
      qc.setQueryData(timesheetKeys.week(weekStart, employeeId), data);
      // 2026-06-04: was timesheetKeys.inbox() only. Flushing the whole
      // root catches the calendar's `["timesheet","summary",from,to]`
      // worked-day dots (which sit outside the inbox subtree) so they
      // refresh after manager approval/reject/unlock.
      void qc.invalidateQueries({ queryKey: timesheetKeys.all });
      // The Inbox / tab badges count submitted weeks (phantom-count fix).
      invalidateApprovalsSummary(qc);
    },
  });
}

/**
 * Reject a submitted week with a required `comment`. The comment is
 * trimmed by the caller before this hook is invoked; an empty string
 * here would 400 server-side (`field: comment`). On success the week
 * detail seeds + inbox invalidates (row → Rejected tab; the original
 * employee's week status banner flips to "Rejected — edit and resubmit"
 * on the next read).
 */
export function useRejectWeek(weekStart: string, employeeId: number) {
  const qc = useQueryClient();
  return useMutation<WeekResponse, unknown, string>({
    mutationFn: (comment: string) =>
      timesheetApi.rejectWeek(weekStart, employeeId, comment),
    onSuccess: (data) => {
      qc.setQueryData(timesheetKeys.week(weekStart, employeeId), data);
      // 2026-06-04: was timesheetKeys.inbox() only. Flushing the whole
      // root catches the calendar's `["timesheet","summary",from,to]`
      // worked-day dots (which sit outside the inbox subtree) so they
      // refresh after manager approval/reject/unlock.
      void qc.invalidateQueries({ queryKey: timesheetKeys.all });
      // The Inbox / tab badges count submitted weeks (phantom-count fix).
      invalidateApprovalsSummary(qc);
    },
  });
}

/**
 * Re-open an approved week (HR-admin only on the backend). The server
 * transitions approved → draft and clears the approver fields; we seed
 * the detail cache + invalidate the inbox so the row disappears from
 * Approved (drafts are not surfaced in the inbox; the employee will
 * re-submit and it'll reappear under Submitted).
 *
 * Destructive intent: amber, not red. The week becomes editable again,
 * but no records are deleted — every cell is preserved, just unlocked.
 */
export function useUnlockWeek(weekStart: string, employeeId: number) {
  const qc = useQueryClient();
  return useMutation<WeekResponse, unknown, string>({
    mutationFn: (comment: string) =>
      timesheetApi.unlockWeek(weekStart, employeeId, comment),
    onSuccess: (data) => {
      qc.setQueryData(timesheetKeys.week(weekStart, employeeId), data);
      // 2026-06-04: was timesheetKeys.inbox() only. Flushing the whole
      // root catches the calendar's `["timesheet","summary",from,to]`
      // worked-day dots (which sit outside the inbox subtree) so they
      // refresh after manager approval/reject/unlock.
      void qc.invalidateQueries({ queryKey: timesheetKeys.all });
      // The Inbox / tab badges count submitted weeks (phantom-count fix).
      invalidateApprovalsSummary(qc);
    },
  });
}

// ---------------------------------------------------------------------------
// Tenant timesheet settings (085 / docs/47) — loose/strict + standard-day.
// GET is any-authed (the My Time allocation preview reads the standard-day
// fallback); PATCH is HR-admin server-side. Mirrors use-attendance.ts's
// settings hooks.
// ---------------------------------------------------------------------------

const SETTINGS_STALE = 5 * 60 * 1000;

export function useTimesheetSettings(enabled = true) {
  return useQuery({
    queryKey: timesheetKeys.settings(),
    queryFn: ({ signal }) => timesheetApi.getTimesheetSettings(signal),
    enabled,
    staleTime: SETTINGS_STALE,
  });
}

export function useUpdateTimesheetSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateTimesheetSettingsRequest) =>
      timesheetApi.updateTimesheetSettings(body),
    onSuccess: (data) => {
      qc.setQueryData(timesheetKeys.settings(), data);
    },
  });
}
